import _ from 'underscore';

/*
* Returns a copy of the passed in Date with hours/minutes/seconds/milliseconds set to zero.
* @param {Date} date - Date to remove time information from
* @returns {Date} - Copy of passed in date
*/
export function withoutTime(date) {
  if (!date) {
    return date;
  }

  const returnDate = new Date(date.valueOf());
  returnDate.setHours(0, 0, 0, 0);
  return returnDate;
}

/**
 * Validates if the date range passed in is incorrect. It ignores the time values.
 * @param { Date } [minDate] - The minimum date that can be selected.
 * @param { Date } [maxDate] - The maximum date that can be selected.
 * @param { Date } [selectedDate] - The date to pre-select in the datepicker.
 * @returns {String[]} - Array of error strings.
 * @throws Will throw if passed in date range is not valid.
 */
export function validateDateRange(minDate, maxDate, selectedDate) {
  const selected = withoutTime(selectedDate);
  const min = withoutTime(minDate);
  const max = withoutTime(maxDate);

  const errors = [];

  if (min && max && min > max) {
    errors.push('MinDateLaterThanMaxDate');
  }

  if (selected && min && selected < min) {
    errors.push('MinDateLaterThanSelectedDate');
  }

  if (selected && max && selected > max) {
    errors.push('SelectedDateLaterThanMaxDate');
  }

  return errors;
}

/**
 * Private function to check if date is valid
 * @param {Object} date - Object to check
 * @returns {Boolean} - True if object is a valid date
 * @private
*/
function isValidDate(date) {
  return _.isDate(date);
}

/**
 * Get the date N days ago
 * @param {Date} dateTime - The input date object
 * @param {Number} [lookBackDays=0] - Number of days to remove
 * @returns {Date} - date object with new parameters, null if date is invalid
 */
export function lastNDate(dateTime, lookBackDays = 0) {
  return isValidDate(dateTime) ?
    new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate() - lookBackDays) : null;
}

/**
 * Get first date of the month N months ago
 * @param {Date} dateTime - The input date object
 * @param {Number} [lookBackMonths=0] - Number of months to remove
 * @returns {Date} - Date object with new parameters
 */
export function firstDateNMonthsAgo(dateTime, lookBackMonths = 0) {
  return isValidDate(dateTime) ?
    new Date(dateTime.getFullYear(), dateTime.getMonth() - lookBackMonths, 1) : null;
}

/**
 * Get last date of the month N months ago
 * @param {Date} dateTime - The input date object
 * @param {Number} [lookBackMonths=0] - Number of months to remove
 * @returns {Date} - Date object with new parameters
 */
export function lastDateNMonthsAgo(dateTime, lookBackMonths = 0) {
  return isValidDate(dateTime) ?
    lastNDate(firstDateNMonthsAgo(dateTime, lookBackMonths - 1), 1) : null;
}

/**
 * Get today for N years ago
 * @param {Date} dateTime - The input date object
 * @param {Number} [lookBackYears=0] - Number of years to remove
 * @returns {Date} - Date object with new parameters
 */
export function todayNYearsAgo(dateTime, lookBackYears = 0) {
  return isValidDate(dateTime) ? new Date(dateTime.getFullYear() - lookBackYears,
    dateTime.getMonth(), dateTime.getDate()) : null;
}

/**
 * Get first date of the year N years ago
 * @param {Date} dateTime - The input date object
 * @param {Number} [lookBackYears=0] - Number of years to remove
 * @returns {Date} - Date object with new parameters
 */
export function firstDateNYearsAgo(dateTime, lookBackYears = 0) {
  return isValidDate(dateTime) ? new Date(dateTime.getFullYear() - lookBackYears, 0, 1) : null;
}

/**
 * Get the current UTC time
 * @returns {String} - the current utc datetime
 */
export function getUtcTime() {
  return new Date().toJSON();
}

/**
 * Get the current timestamp
 * @returns {Date} - the timestamp since epoch in milliseconds
 */
export function getTimestamp() {
  if (window.performance && window.performance.now && window.performance.timing) {
    return window.performance.timing.navigationStart + window.performance.now();
  }

  return Date.now();
}



// WEBPACK FOOTER //
// ./d:/dbs/el/aapusc/private/client/packages/primitive-utilities/src/date.es